import { DestroyRef, Injectable, signal, inject, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, tap, switchMap, of, throwError } from 'rxjs';
import { map, shareReplay, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import { CookieService } from './cookie.service';
import {
  LoginRequest,
  LoginResponse,
  VerificationCodeRequest,
  CallbackResponse,
  RefreshTokenResponse,
  AuthTokens,
  STORAGE_KEYS,
} from '../models/auth.models';
import { isJwtExpired } from '../utils/jwt.util';

import { ProfileService } from './profile.service';

const AUTH_BROADCAST_CHANNEL = 'sgi-auth-sync';

type AuthBroadcastMessage =
  | { type: 'tokens-updated' }
  | { type: 'signed-out' };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);
  private readonly cookieService = inject(CookieService);
  private readonly profileService = inject(ProfileService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly accessToken = signal<string | null>(null);
  private readonly refreshToken = signal<string | null>(null);
  private sessionResetInProgress = false;
  /** true entre início do logout e limpeza local — bloqueia setAuthTokens. */
  private logoutInProgress = false;
  /** Incrementado em sign-out; respostas de refresh antigas são descartadas. */
  private sessionGeneration = 0;
  private refreshSubscription: Observable<string> | null = null;
  private authChannel: BroadcastChannel | null = null;

  readonly isAuthenticated = computed(() => !!this.accessToken());

  constructor() {
    const destroyRef = inject(DestroyRef);

    this.syncFromCookieToStorage();
    this.syncTokensFromCookies();

    if (!this.isBrowser) {
      return;
    }

    const onFocus = () => this.syncTokensFromCookies();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.syncTokensFromCookies();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (typeof BroadcastChannel !== 'undefined') {
      this.authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      this.authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
        if (event.data?.type === 'signed-out') {
          if (!this.sessionResetInProgress && !this.logoutInProgress) {
            this.sessionGeneration += 1;
            this.refreshSubscription = null;
            this.clearAuthDataLocalOnly();
            this.profileService.clearProfile();
            void this.router.navigate(['/login']);
          }
          return;
        }
        if (event.data?.type === 'tokens-updated') {
          if (!this.logoutInProgress && !this.sessionResetInProgress) {
            this.syncTokensFromCookies();
          }
        }
      };
    }

    destroyRef.onDestroy(() => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      this.authChannel?.close();
      this.authChannel = null;
    });
  }

  isSessionInvalidated(): boolean {
    return this.sessionResetInProgress || this.logoutInProgress;
  }

  /** Compatibilidade com cookies gravados por outras apps do ecossistema. */
  private syncFromCookieToStorage(): void {
    const tokenInStorage = this.storage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
    const tokenInCookie = this.cookieService.get(STORAGE_KEYS.ACCESS_TOKEN);

    if (!tokenInStorage && tokenInCookie) {
      this.storage.set(STORAGE_KEYS.ACCESS_TOKEN, tokenInCookie, true);
    }

    const refreshInStorage = this.storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
    const refreshInCookie = this.cookieService.get(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshInStorage && refreshInCookie) {
      this.storage.set(STORAGE_KEYS.REFRESH_TOKEN, refreshInCookie, true);
    }
  }

  private readAccessToken(): string | null {
    return this.storage.get<string>(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private readRefreshToken(): string | null {
    return this.storage.get<string>(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * O access_token some do cookie quando o JWT expira (Max-Age = exp).
   * Mantém o refresh em memória para guard/interceptor renovarem a sessão.
   */
  syncTokensFromCookies(): void {
    const access = this.readAccessToken();
    const refresh = this.readRefreshToken();

    if (access && !isJwtExpired(access)) {
      this.accessToken.set(access);
      if (refresh && !isJwtExpired(refresh)) {
        this.refreshToken.set(refresh);
      }
      return;
    }

    this.accessToken.set(null);

    if (refresh && !isJwtExpired(refresh)) {
      this.refreshToken.set(refresh);
      return;
    }

    if (this.refreshToken()) {
      this.refreshToken.set(null);
    }
  }

  /** Leitura pura — segura dentro de `computed` (sem escrever em signals). */
  peekAccessToken(): string | null {
    const token = this.readAccessToken();
    if (!token || isJwtExpired(token)) {
      return null;
    }
    return token;
  }

  /** Leitura pura — segura dentro de `computed` (sem escrever em signals). */
  peekRefreshToken(): string | null {
    const token = this.readRefreshToken();
    if (!token || isJwtExpired(token)) {
      return null;
    }
    return token;
  }

  getAccessToken(): string | null {
    const token = this.peekAccessToken();
    if (!token) {
      this.accessToken.set(null);
      return null;
    }
    if (this.accessToken() !== token) {
      this.accessToken.set(token);
    }
    return token;
  }

  /** Sempre lê do cookie — o signal pode estar desatualizado após expiração do access. */
  getRefreshToken(): string | null {
    const token = this.peekRefreshToken();
    if (!token) {
      return null;
    }
    if (this.refreshToken() !== token) {
      this.refreshToken.set(token);
    }
    return token;
  }

  sendAccessCode(email: string): Observable<LoginResponse> {
    const body: LoginRequest = { email };
    return this.http.post<LoginResponse>(`${this.apiUrl}/autenticacao/login`, body);
  }

  verifyAccessCode(codigo: string): Observable<string> {
    const body: VerificationCodeRequest = { codigo };
    return this.http
      .post<CallbackResponse>(`${this.apiUrl}/autenticacao/callback`, body)
      .pipe(
        tap((response) => {
          const access =
            response.access_token ?? response.accessToken ?? '';
          const refresh =
            response.refresh_token ?? response.refreshToken;
          if (!access) {
            throw new Error('Resposta do callback sem access_token');
          }
          this.setAuthTokens({
            accessToken: access,
            refreshToken: refresh,
          });
        }),
        switchMap(() => this.profileService.getUserProfile()),
        map(() => this.accessToken()!)
      );
  }

  /**
   * Adia a navegação um tick para reduzir corrida com extensões do navegador
   * que escutam mensagens durante unload da página (ex.: /callback).
   */
  redirectAfterLogin(): void {
    if (!environment.production) {
      console.log('[AuthService] Redirecionando após login para /aplicacoes');
    }
    void this.router.navigate(['/aplicacoes']);
  }

  /** Redirecionamento após OAuth (/callback) — adia 1 tick por causa de extensões do navegador. */
  redirectAfterOAuthCallback(): void {
    queueMicrotask(() => this.redirectAfterLogin());
  }

  refreshAccessToken(): Observable<string> {
    if (this.sessionResetInProgress || this.logoutInProgress) {
      return throwError(() => new Error('Sessão encerrada'));
    }

    if (this.refreshSubscription) {
      return this.refreshSubscription;
    }

    const token = this.getRefreshToken();

    if (!token) {
      return throwError(() => new Error('Refresh token ausente'));
    }

    const generationAtStart = this.sessionGeneration;

    this.refreshSubscription = this.http
      .post<RefreshTokenResponse>(`${this.apiUrl}/autenticacao/refresh`, { refresh_token: token })
      .pipe(
        switchMap((response) => {
          if (generationAtStart !== this.sessionGeneration || this.sessionResetInProgress || this.logoutInProgress) {
            return throwError(() => new Error('Sessão encerrada durante refresh'));
          }

          this.setAuthTokens({
            accessToken: response.access_token,
            refreshToken: response.refresh_token ?? token,
          });

          if (!this.profileService.loaded()) {
            return this.profileService.getUserProfile().pipe(
              map(() => response.access_token)
            );
          }

          return of(response.access_token);
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshSubscription = null;
        })
      );

    return this.refreshSubscription;
  }

  logout(): void {
    if (this.sessionResetInProgress || this.logoutInProgress) {
      return;
    }

    const access = this.getAccessToken();
    const refresh = this.getRefreshToken();

    this.logoutInProgress = true;
    // Cancela refresh em voo para não regravar tokens após o sign-out.
    this.sessionGeneration += 1;
    this.refreshSubscription = null;

    const finish = () => this.signOutLocally();

    if (!access && !refresh) {
      finish();
      return;
    }

    const revoke = (bearer: string) =>
      this.http.delete(`${this.apiUrl}/autenticacao/logout`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });

    if (access) {
      revoke(access).subscribe({ next: () => finish(), error: () => finish() });
      return;
    }

    // Access expirado: obtém Bearer via refresh (endpoint público) só para revogar.
    this.http
      .post<RefreshTokenResponse>(`${this.apiUrl}/autenticacao/refresh`, {
        refresh_token: refresh!,
      })
      .pipe(switchMap((response) => revoke(response.access_token)))
      .subscribe({ next: () => finish(), error: () => finish() });
  }

  /** Encerra sessão local sem nova chamada HTTP (uso em guard/interceptor). */
  signOutLocally(): void {
    if (this.sessionResetInProgress) {
      return;
    }

    this.sessionResetInProgress = true;
    this.logoutInProgress = true;
    this.sessionGeneration += 1;
    this.refreshSubscription = null;
    this.clearAuthData();
    this.profileService.clearProfile();
    this.broadcast({ type: 'signed-out' });

    const navigate = () => {
      void this.router.navigate(['/login']).finally(() => {
        this.sessionResetInProgress = false;
        this.logoutInProgress = false;
      });
    };

    if (!this.isBrowser) {
      navigate();
      return;
    }

    queueMicrotask(navigate);
  }

  setAuthTokens(tokens: AuthTokens): void {
    if (this.sessionResetInProgress || this.logoutInProgress) {
      return;
    }

    if (tokens.accessToken) {
      this.storage.set(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken, true);
      this.accessToken.set(tokens.accessToken);
    }

    if (tokens.refreshToken) {
      this.storage.set(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken, true);
      this.refreshToken.set(tokens.refreshToken);
    }

    this.broadcast({ type: 'tokens-updated' });
  }

  private clearAuthData(): void {
    this.clearAuthDataLocalOnly();
  }

  private clearAuthDataLocalOnly(): void {
    this.storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    this.storage.remove(STORAGE_KEYS.REFRESH_TOKEN);

    this.accessToken.set(null);
    this.refreshToken.set(null);
  }

  private broadcast(message: AuthBroadcastMessage): void {
    try {
      this.authChannel?.postMessage(message);
    } catch {
      // Canal fechado ou indisponível — sync por focus ainda cobre.
    }
  }
}
